import { useEffect, useRef, useState } from "react";
import { api, type User } from "../api";
import { notify, pauseNotifications, pausedUntil } from "../notify";
import { playSound, SOUNDS } from "../sounds";
import { applyBoot, useStore } from "../store";
import { Avatar, toast, useMe } from "./ui";
import { isDesktop } from "../desktop";

const COLORS = ["#e8912d", "#04ac79", "#4f94e8", "#b07ce9", "#e65e73", "#07a4ba", "#b88a05"];
const QUICK = ["🌴 Off today", "📞 In a call", "🍽 At lunch", "✈️ Travelling", "🤒 Off sick"];

const saveUser = (u: User) => useStore.setState((s) => ({ me: s.me?.id === u.id ? u : s.me, users: { ...s.users, [u.id]: u } }));

/** Crops the middle square and shrinks it to 256 px, so photos are small and sharp. */
async function squareJpeg(file: File): Promise<File> {
  const img = await createImageBitmap(file);
  const side = Math.min(img.width, img.height), c = document.createElement("canvas");
  c.width = c.height = 256;
  c.getContext("2d")!.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, 256, 256);
  const blob = await new Promise<Blob>((r) => c.toBlob((b) => r(b!), "image/jpeg", 0.9));
  return new File([blob], "photo.jpg", { type: "image/jpeg" });
}

/** Your profile, from your name at the bottom left: photo, status, pausing notifications, colour. */
export function Profile({ onClose, onSignOut }: { onClose: () => void; onSignOut: () => void }) {
  const me = useMe();
  const [status, setStatus] = useState(me.status ?? "");
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(pausedUntil());
  const box = useRef<HTMLDivElement>(null);
  const pick = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const c = (e: MouseEvent) => { const t = e.target as HTMLElement; if (!box.current?.contains(t) && !t.closest(".mebtn")) onClose(); };
    addEventListener("keydown", k); addEventListener("mousedown", c);
    return () => { removeEventListener("keydown", k); removeEventListener("mousedown", c); };
  }, [onClose]);

  const update = async (p: Parameters<typeof api.updateMe>[0]) => {
    try { saveUser(await api.updateMe(p)); } catch (e: any) { toast(e.message || "Couldn't save"); }
  };
  const photo = async (f: File | undefined) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast("Pick an image");
    setBusy(true);
    try { const up = await api.upload(await squareJpeg(f)); await update({ avatarFileId: up.id }); }
    catch (e: any) { toast(e.message || "Upload failed"); }
    setBusy(false);
  };
  const saveStatus = (s: string) => { setStatus(s); update({ status: s || null }); };
  const pause = (ms: number | "morning" | 0) => {
    let until = 0;
    if (ms === "morning") { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(8, 0, 0, 0); until = d.getTime(); }
    else if (ms) until = Date.now() + ms;
    pauseNotifications(until); setPaused(until);
  };
  const uploaded = me.avatarUrl?.startsWith("/api/files/");

  return (
    <div className="prof" ref={box}>
      <div className="top">
        <button className="ph" title="Change photo" onClick={() => pick.current?.click()}><Avatar user={me} size={64} />{busy && <span className="ld">…</span>}</button>
        <div><b>{me.name}</b><small>{me.email}</small></div>
        <input ref={pick} type="file" accept="image/*" hidden onChange={(e) => { photo(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      <div className="opt">
        <button onClick={() => pick.current?.click()}>Change photo<span>{busy ? "uploading…" : "upload"}</span></button>
        {uploaded && <button onClick={() => update({ avatarFileId: null })}>Use my Google photo<span>or your initial</span></button>}
      </div>
      <h5>Status</h5>
      <input className="stin" placeholder="What are you up to?" value={status} maxLength={80}
        onChange={(e) => setStatus(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveStatus(status.trim())} onBlur={() => status.trim() !== (me.status ?? "") && saveStatus(status.trim())} />
      <div className="chips" style={{ marginTop: 8 }}>
        {QUICK.map((q) => <button key={q} className={me.status === q ? "on" : ""} onClick={() => saveStatus(q)}>{q}</button>)}
        {me.status && <button onClick={() => saveStatus("")}>Clear</button>}
      </div>
      <h5>Notify me about</h5>
      <div className="chips">
        {([["all", "Every message"], ["dms", "DMs and @mentions"], ["none", "Nothing"]] as const).map(([v, l]) =>
          <button key={v} className={me.notifyDefault === v ? "on" : ""} onClick={async () => { await update({ notifyDefault: v }); applyBoot(await api.boot()); }}>{l}</button>)}
      </div>
      <div className="note">Any chat can still be set its own way from its bell.</div>
      <h5>Sound</h5>
      <div className="sndrow">
        <select value={me.sound} onChange={(e) => { playSound(e.target.value, me.volume); update({ sound: e.target.value }); }}>
          {SOUNDS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
        </select>
        <button className="play" title="Play" onClick={() => playSound(me.sound, me.volume)}>▶</button>
        <div className="chips">
          {([[0.5, "Quiet"], [1, "Normal"], [1.6, "Loud"]] as const).map(([v, l]) =>
            <button key={l} className={Math.abs(me.volume - v) < 0.01 ? "on" : ""} onClick={() => { playSound(me.sound, v); update({ volume: v }); }}>{l}</button>)}
        </div>
      </div>
      <button className="testn" onClick={() => setTimeout(() => notify("Rockenue Hub", "This is how a new message will look and sound.", useStore.getState().route, "test", true), 3000)}>
        Send me a test notification <small>in 3 seconds (switch to another app to see it)</small></button>
      <h5>Pause</h5>
      <div className="chips">
        {paused > Date.now()
          ? <><span className="pz">Paused until {new Date(paused).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" })}</span><button onClick={() => pause(0)}>Resume</button></>
          : <><button onClick={() => pause(3600_000)}>Pause 1 hour</button><button onClick={() => pause("morning")}>Until tomorrow 8:00</button></>}
      </div>
      <h5>Your colour (shows when there's no photo)</h5>
      <div className="cols">{COLORS.map((c) => <i key={c} style={{ background: c }} className={me.color === c ? "sel" : ""} onClick={() => update({ color: c })} />)}</div>
      <div className="foot">{!isDesktop() && <a href="/desktop/mac">Get the Mac app</a>}<button onClick={onSignOut}>Sign out</button></div>
    </div>
  );
}
